import { computeFailedStreakDetails, FAILED_OUTCOMES } from '../contactMoments/computeFailedStreak'

// AI-CONTEXT: See docs/features/household-overview-table-and-statuses.md
// Rolls the ContactMoments collection up to one entry per household, so the
// overview can show "last contact" without opening every household one by one.
//
// The streak and the moment it started both come from
// computeFailedStreakDetails — the same rule the mark-unreachable button uses.
// Two counters that can disagree about what "three failed attempts" means would
// be worse than none.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook

/**
 * Sort key for one contact moment, newest first.
 *
 * A document written moments ago still has `Timestamp: null` locally —
 * Firestore fills serverTimestamp() in on the server. That document is the
 * newest one, not the oldest, so it sorts to the front.
 *
 * @param {{Timestamp?: {toDate: () => Date}}} moment
 * @returns {number}
 */
const sortTime = (moment) => {
    if (typeof moment?.Timestamp?.toDate !== 'function') return Infinity
    const time = moment.Timestamp.toDate().getTime()
    return Number.isNaN(time) ? Infinity : time
}

/**
 * @typedef {object} LatestContact
 * @property {{toDate: () => Date}|null} timestamp - When the newest attempt happened
 * @property {string} outcome - Raw ContactMoments.Outcome value
 * @property {string} type - Raw ContactMoments.Type value
 * @property {number} failedStreak - Consecutive failed attempts, counted back from the newest
 * @property {{toDate: () => Date}|null} firstFailedAt - Start of that failed run, or null when the streak is 0
 */

/**
 * @param {Array<object>} [contactMoments] - Every ContactMoments doc of the tenant
 * @returns {Record<string, LatestContact>} Keyed by AdressID
 */
const latestContactPerAdress = (contactMoments) => {
    /** @type {Record<string, Array<object>>} */
    const byAdress = {}

    ;(Array.isArray(contactMoments) ? contactMoments : []).forEach((moment) => {
        if (!moment?.AdressID) return
        if (!byAdress[moment.AdressID]) byAdress[moment.AdressID] = []
        byAdress[moment.AdressID].push(moment)
    })

    /** @type {Record<string, LatestContact>} */
    const result = {}

    Object.entries(byAdress).forEach(([adressId, moments]) => {
        // computeFailedStreak walks from the newest backwards, so the list has
        // to be sorted descending before it can say anything useful.
        const sorted = [...moments].sort((a, b) => sortTime(b) - sortTime(a))
        const newest = sorted[0]
        const { streak, firstFailedAt } = computeFailedStreakDetails(sorted)

        result[adressId] = {
            timestamp: newest.Timestamp || null,
            outcome: newest.Outcome || '',
            type: newest.Type || '',
            failedStreak: streak,
            firstFailedAt,
        }
    })

    return result
}

export default latestContactPerAdress
export { FAILED_OUTCOMES }
